import {
  EmailComposerDraft,
  EmailListFilter,
  EmailMailboxId,
  IEmailMessage,
} from '@dataTypes/email.type'
import { FilterSelection } from '@components/common/SearchWithFilter/SearchWithFilter.component'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  fetchEmailMessages,
  fetchEmailSettings,
  fetchEmailStarTags,
} from '@store/actions/email.action'
import type { EmailSettingsData, EmailStarTag } from '@services/api/email.api'

export type EmailState = {
  activeMailbox: EmailMailboxId
  /** When set, the list shows only messages with this `folderId` (custom Folders). */
  activeCustomFolderId: string | null
  /** When set, the list shows only messages with this label id. */
  activeLabelId: string | null
  listFilter: EmailListFilter
  sortBy: string
  searchQuery: string
  /** Search-bar dropdown filters (Status / Labels). Independent from `listFilter` tabs. */
  filters: FilterSelection
  messages: IEmailMessage[]
  starTags: EmailStarTag[]
  selectedIds: string[]
  messagesLoading: boolean
  messagesError: string | null
  settings: EmailSettingsData | null
}

const initialState: EmailState = {
  activeMailbox: 'inbox',
  activeCustomFolderId: null,
  activeLabelId: null,
  listFilter: 'all',
  sortBy: 'date',
  searchQuery: '',
  filters: {
    status: [],
    labels: [],
  },
  messages: [],
  starTags: [],
  selectedIds: [],
  messagesLoading: false,
  messagesError: null,
  settings: null,
}

const emailSlice = createSlice({
  name: 'email',
  initialState,
  reducers: {
    setEmailMailbox: (state, action: PayloadAction<EmailMailboxId>) => {
      state.activeMailbox = action.payload
      state.activeCustomFolderId = null
      state.activeLabelId = null
      state.selectedIds = []
    },
    setEmailActiveCustomFolder: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      state.activeCustomFolderId = action.payload
      state.activeLabelId = null
      state.selectedIds = []
    },
    setEmailActiveLabel: (state, action: PayloadAction<string | null>) => {
      state.activeLabelId = action.payload
      state.activeCustomFolderId = null
      state.selectedIds = []
    },
    setEmailListFilter: (state, action: PayloadAction<EmailListFilter>) => {
      state.listFilter = action.payload
    },
    setEmailSortBy: (state, action: PayloadAction<string>) => {
      state.sortBy = action.payload
    },
    setEmailSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload
    },
    setEmailFilters: (state, action: PayloadAction<FilterSelection>) => {
      state.filters = action.payload
    },
    toggleEmailSelection: (state, action: PayloadAction<string>) => {
      const id = action.payload
      const set = new Set(state.selectedIds)
      if (set.has(id)) set.delete(id)
      else set.add(id)
      state.selectedIds = Array.from(set)
    },
    clearEmailSelection: (state) => {
      state.selectedIds = []
    },
    resetEmailState: (state) => {
      state.activeMailbox = 'inbox'
      state.activeCustomFolderId = null
      state.activeLabelId = null
      state.listFilter = 'all'
      state.sortBy = 'date'
      state.searchQuery = ''
      state.filters = { status: [], labels: [] }
      state.messages = []
      state.starTags = []
      state.selectedIds = []
      state.messagesLoading = false
      state.messagesError = null
      state.settings = null
    },
    setEmailSelectAll: (
      state,
      action: PayloadAction<{ ids: string[]; select: boolean }>,
    ) => {
      if (!action.payload.select) {
        state.selectedIds = []
        return
      }
      state.selectedIds = action.payload.ids
    },
    setEmailStarTag: (
      state,
      action: PayloadAction<{
        id: string
        starTagId: string | null
        starTag: IEmailMessage['starTag']
      }>,
    ) => {
      const row = state.messages.find((m) => m.id === action.payload.id)
      if (!row) return
      row.starTagId = action.payload.starTagId
      row.starTag = action.payload.starTag
      row.isStarred = Boolean(action.payload.starTagId)
    },
    setEmailReadStatus: (
      state,
      action: PayloadAction<{ ids: string[]; isRead: boolean }>,
    ) => {
      const { ids, isRead } = action.payload
      const idSet = new Set(ids)
      state.messages.forEach((message) => {
        if (idSet.has(message.id)) {
          message.isRead = isRead
        }
      })
    },
    setEmailMailboxForIds: (
      state,
      action: PayloadAction<{ ids: string[]; mailbox: EmailMailboxId }>,
    ) => {
      const { ids, mailbox } = action.payload
      const idSet = new Set(ids)
      state.messages.forEach((message) => {
        if (idSet.has(message.id)) {
          message.mailbox = mailbox
          delete message.folderId
        }
      })
      state.selectedIds = state.selectedIds.filter((id) => !idSet.has(id))
    },
    moveEmailsToCustomFolder: (
      state,
      action: PayloadAction<{ ids: string[]; folderId: string }>,
    ) => {
      const { ids, folderId } = action.payload
      const idSet = new Set(ids)
      state.messages.forEach((message) => {
        if (idSet.has(message.id)) {
          message.folderId = folderId
        }
      })
      state.selectedIds = []
    },
    clearEmailMessagesFolderId: (state, action: PayloadAction<string>) => {
      const folderId = action.payload
      state.messages.forEach((message) => {
        if (message.folderId === folderId) {
          delete message.folderId
        }
      })
      if (state.activeCustomFolderId === folderId) {
        state.activeCustomFolderId = null
      }
    },
    permanentlyDeleteEmails: (
      state,
      action: PayloadAction<{ ids: string[] }>,
    ) => {
      const idSet = new Set(action.payload.ids)
      state.messages = state.messages.filter(
        (message) => !idSet.has(message.id),
      )
      state.selectedIds = state.selectedIds.filter((id) => !idSet.has(id))
    },
    updateEmailLabel: (
      state,
      action: PayloadAction<{ id: string; name: string; color: string }>,
    ) => {
      const { id, name, color } = action.payload
      state.messages.forEach((message) => {
        message.labels = message.labels.map((lb) =>
          lb.id === id ? { ...lb, name, color } : lb,
        )
      })
    },
    deleteEmailLabel: (state, action: PayloadAction<{ id: string }>) => {
      const { id } = action.payload
      state.messages.forEach((message) => {
        message.labels = message.labels.filter((lb) => lb.id !== id)
      })
      if (state.activeLabelId === id) {
        state.activeLabelId = null
      }
    },
    setEmailLabelForIds: (
      state,
      action: PayloadAction<{
        ids: string[]
        label: { id: string; name: string; color: string }
        setChecked: boolean
      }>,
    ) => {
      const {
        ids,
        label: { id: labelId, name, color },
        setChecked,
      } = action.payload
      const idSet = new Set(ids)
      state.messages.forEach((message) => {
        if (!idSet.has(message.id)) return
        const hasLabel = message.labels.some((lb) => lb.id === labelId)
        if (setChecked && !hasLabel) {
          message.labels = [...message.labels, { id: labelId, name, color }]
        } else if (!setChecked && hasLabel) {
          message.labels = message.labels.filter((lb) => lb.id !== labelId)
        }
      })
    },
    upsertEmailDraftFromComposer: (
      state,
      action: PayloadAction<{ id: string; draft: EmailComposerDraft }>,
    ) => {
      const { id, draft } = action.payload
      const subject = draft.subject.trim() || '(no subject)'
      const plainSnippet = draft.body
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
      const snippet = plainSnippet || 'Draft saved'

      const nextMessage: IEmailMessage = {
        id,
        mailbox: 'drafts',
        from: 'You',
        subject,
        snippet,
        dateLabel: 'Now',
        isRead: true,
        isStarred: false,
        starTagId: null,
        starTag: null,
        hasAttachment: (draft.attachments?.length ?? 0) > 0,
        labels: [],
        composeDraft: draft,
        attachments: draft.attachments,
        toRecipients: draft.to,
        ccRecipients: draft.cc,
        bccRecipients: draft.bcc,
        bodyHtml: draft.body,
      }

      const index = state.messages.findIndex((m) => m.id === id)
      if (index >= 0) {
        state.messages[index] = {
          ...state.messages[index],
          ...nextMessage,
        }
      } else {
        state.messages.unshift(nextMessage)
      }
    },
    upsertSentEmailFromComposer: (
      state,
      action: PayloadAction<{
        id: string
        payload: {
          to: string[]
          cc: string[]
          bcc: string[]
          attachments?: {
            id: string
            name: string
            sizeLabel: string
            mimeType?: string
          }[]
          subject: string
          body: string
          draftMessageId?: string
        }
      }>,
    ) => {
      const {
        id,
        payload: { to, cc, bcc, attachments, subject, body, draftMessageId },
      } = action.payload
      const plainSnippet = body
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
      const snippet = plainSnippet || 'Sent email'

      const nextMessage: IEmailMessage = {
        id,
        mailbox: 'sent',
        from: 'You',
        subject: subject.trim() || '(no subject)',
        snippet,
        dateLabel: 'Now',
        isRead: true,
        isStarred: false,
        starTagId: null,
        starTag: null,
        hasAttachment: (attachments?.length ?? 0) > 0,
        labels: [],
        toRecipients: to,
        ccRecipients: cc,
        bccRecipients: bcc,
        bodyHtml: body,
        attachments,
      }
      const index = state.messages.findIndex((m) => m.id === id)
      if (index >= 0) {
        state.messages[index] = {
          ...state.messages[index],
          ...nextMessage,
        }
      } else {
        state.messages.unshift(nextMessage)
      }
      if (draftMessageId) {
        state.messages = state.messages.filter((m) => m.id !== draftMessageId)
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmailMessages.pending, (state, action) => {
        // Background polls pass `silent` so the list skeleton does not flash.
        if (!action.meta.arg?.silent) {
          state.messagesLoading = true
          // Drop placeholder mock rows so an empty/failed fetch shows the sync prompt.
          state.messages = []
        }
        state.messagesError = null
      })
      .addCase(
        fetchEmailMessages.fulfilled,
        (state, action: PayloadAction<IEmailMessage[]>) => {
          state.messagesLoading = false
          state.messagesError = null
          state.messages = action.payload
        },
      )
      .addCase(fetchEmailMessages.rejected, (state, action) => {
        state.messagesLoading = false
        state.messagesError =
          (action.payload as string | undefined) ??
          action.error.message ??
          'Failed to load emails'
      })
      .addCase(fetchEmailSettings.fulfilled, (state, action) => {
        state.settings = action.payload
      })
      .addCase(
        fetchEmailStarTags.fulfilled,
        (state, action: PayloadAction<EmailStarTag[]>) => {
          state.starTags = action.payload
        },
      )
  },
})

export const {
  setEmailMailbox,
  setEmailActiveCustomFolder,
  setEmailActiveLabel,
  setEmailListFilter,
  setEmailSortBy,
  toggleEmailSelection,
  clearEmailSelection,
  resetEmailState,
  setEmailSelectAll,
  setEmailStarTag,
  setEmailSearchQuery,
  setEmailFilters,
  setEmailReadStatus,
  setEmailMailboxForIds,
  moveEmailsToCustomFolder,
  clearEmailMessagesFolderId,
  permanentlyDeleteEmails,
  updateEmailLabel,
  deleteEmailLabel,
  setEmailLabelForIds,
  upsertEmailDraftFromComposer,
  upsertSentEmailFromComposer,
} = emailSlice.actions

export default emailSlice.reducer
